import React, { useState, useEffect } from 'react';
import { Select, Button, Checkbox, Table, message } from 'antd';
import axios from 'axios';
import { BASE_URL } from '../../middleware/api';
import './ImageManagementPage.css';

const { Option } = Select;

const ImageManagementPage = () => {
    const [selectedAgency, setSelectedAgency] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState('pending');
    const [showConfirmedImages, setShowConfirmedImages] = useState(false);
    const [sortByLastModify, setSortByLastModify] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [agencies, setAgencies] = useState([]);
    const [loadingAgencies, setLoadingAgencies] = useState(false);
    const [updatingStatus, setUpdatingStatus] = useState(new Set());
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0
    });

    const loadAgencies = async () => {
        setLoadingAgencies(true);

        const url = `${BASE_URL}/assessment_image/agency_list`;

        try {
            const response = await axios.get(url);

            if (response.data && Array.isArray(response.data)) {
                setAgencies(response.data);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoadingAgencies(false);
        }
    };

    const searchOriginalImages = async (params = {}) => {
        setLoading(true);

        const defaultParams = {
            page: 0, // 0-based page number
            size: 20,
            sort: sortByLastModify ? 'lastModify,desc' : 'sightingsCount,desc',
            sightingsCountMin: 1, // як вказано у вимогах
            excludeNullDates: sortByLastModify, // виключаємо null дати коли сортуємо по lastModify
            ...params
        };

        Object.keys(defaultParams).forEach(key =>
            defaultParams[key] === undefined && delete defaultParams[key]
        );

        const queryString = new URLSearchParams(defaultParams).toString();
        const url = `${BASE_URL}/original_image/search?${queryString}`;

        try {
            const response = await axios.get(url);

            const data = response.data;

            if (data && data.content) {
                const transformedData = data.content.map((item, index) => ({
                    key: item.id || index,
                    imageId: item.imageId,
                    image: item.url,
                    sightings: item.sightingsCount,
                    agency: item.agency,
                    fileName: item.fileName,
                    pursuable: item.pursuable,
                    status: item.pursuable ? 'pending' : 'approved',
                    confirmed: item.confirmed || false,
                }));

                setTableData(transformedData);
                setPagination(prev => ({
                    ...prev,
                    total: data.totalElements,
                    current: data.number + 1, // API використовує 0-based, UI 1-based
                }));
            }
        } catch (error) {
            console.error('❌ Request failed:', {
                error,
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        searchOriginalImages({ pursuable: true, confirmed: false });
        loadAgencies();
    }, []);

    const handleSearch = () => {
        const searchParams = {
            page: 0, // Скидуємо на першу сторінку при новому пошуку
            size: pagination.pageSize
        };

        if (selectedAgency) {
            searchParams.agency = selectedAgency;
        }

        if (selectedStatus) {
            const pursuableValue = selectedStatus === 'pending';
            searchParams.pursuable = pursuableValue;
        }

        // Always include confirmed filter
        searchParams.confirmed = showConfirmedImages;

        // Add sorting parameters
        if (sortByLastModify) {
            searchParams.sort = 'lastModify,desc';
            searchParams.excludeNullDates = true;
        }

        setPagination(prev => ({
            ...prev,
            current: 1
        }));

        searchOriginalImages(searchParams);
    };

    // Обробники зміни фільтрів
    const handleAgencyChange = (value) => {
        setSelectedAgency(value);
    };

    const handleFilterStatusChange = (value) => {
        setSelectedStatus(value);
    };

    const handleSortByLastModifyChange = (checked) => {
        setSortByLastModify(checked);

        // Автоматично виконуємо пошук з новими параметрами сортування
        const searchParams = {
            page: 0,
            size: pagination.pageSize
        };

        if (selectedAgency) {
            searchParams.agency = selectedAgency;
        }

        if (selectedStatus) {
            searchParams.pursuable = selectedStatus === 'pending';
        }

        searchParams.confirmed = showConfirmedImages;

        if (checked) {
            searchParams.sort = 'lastModify,desc';
            searchParams.excludeNullDates = true;
        }

        setPagination(prev => ({
            ...prev,
            current: 1
        }));

        searchOriginalImages(searchParams);
    };

    const updatePursuableStatus = async (ids, pursuable) => {
        try {
            const response = await axios.post(`${BASE_URL}/original_image/update-pursuable`, {
                ids: ids,
                pursuable: pursuable
            });

            if (response.status === 200) {
                return response.data;
            }
            throw new Error('Failed to update pursuable status');
        } catch (error) {
            console.error('Error updating pursuable status:', error);
            throw error;
        }
    };

    const updateConfirmedStatus = async (ids, confirmed) => {
        try {
            const response = await axios.post(`${BASE_URL}/original_image/update-confirmed`, {
                ids: ids,
                confirmed: confirmed
            });

            if (response.status === 200) {
                return response.data;
            }
            throw new Error('Failed to update confirmed status');
        } catch (error) {
            console.error('Error updating confirmed status:', error);
            throw error;
        }
    };

    const handleStatusChange = async (value, record) => {
        const newPursuableValue = value === 'pending';

        setUpdatingStatus(prev => new Set(prev).add(record.key));

        try {
            await updatePursuableStatus([record.key], newPursuableValue);

            const statusText = newPursuableValue ? 'Active' : 'Not Pursuable';
            message.success(`Status updated to ${statusText} successfully.`);

            const searchParams = {
                page: pagination.current - 1,
                size: pagination.pageSize
            };

            if (selectedAgency) {
                searchParams.agency = selectedAgency;
            }

            if (selectedStatus) {
                const pursuableValue = selectedStatus === 'pending';
                searchParams.pursuable = pursuableValue;
            }

            // Always include confirmed filter
            searchParams.confirmed = showConfirmedImages;

            // Add sorting parameters
            if (sortByLastModify) {
                searchParams.sort = 'lastModify,desc';
                searchParams.excludeNullDates = true;
            }

            await searchOriginalImages(searchParams);

        } catch (error) {
            console.error('Failed to update status:', error);
            message.error('Failed to update status. Please try again.');
        } finally {
            setUpdatingStatus(prev => {
                const newSet = new Set(prev);
                newSet.delete(record.key);
                return newSet;
            });
        }
    };

    const handleConfirmedChange = async (checked, record) => {
        setUpdatingStatus(prev => new Set(prev).add(record.key));

        try {
            await updateConfirmedStatus([record.key], checked);

            const statusText = checked ? 'confirmed' : 'unconfirmed';
            message.success(`Image marked as ${statusText} successfully.`);

            const searchParams = {
                page: pagination.current - 1,
                size: pagination.pageSize
            };

            if (selectedAgency) {
                searchParams.agency = selectedAgency;
            }

            if (selectedStatus) {
                searchParams.pursuable = selectedStatus === 'pending';
            }

            // Always include confirmed filter
            searchParams.confirmed = showConfirmedImages;

            // Add sorting parameters
            if (sortByLastModify) {
                searchParams.sort = 'lastModify,desc';
                searchParams.excludeNullDates = true;
            }

            await searchOriginalImages(searchParams);

        } catch (error) {
            console.error('Failed to update confirmed status:', error);
            message.error('Failed to update confirmation status. Please try again.');
        } finally {
            setUpdatingStatus(prev => {
                const newSet = new Set(prev);
                newSet.delete(record.key);
                return newSet;
            });
        }
    };

    // Скидання всіх фільтрів
    const handleClearFilters = () => {
        setSelectedAgency(null);
        setSelectedStatus('pending');
        setShowConfirmedImages(false);
        setSortByLastModify(false);

        // Виконуємо пошук без фільтрів
        setPagination(prev => ({
            ...prev,
            current: 1
        }));

        searchOriginalImages({
            page: 0,
            size: pagination.pageSize,
            pursuable: true,
            confirmed: false
        });
    };

    // Обробка зміни сторінки таблиці
    const handleTableChange = (newPagination, filters, sorter) => {
        const searchParams = {};

        // Додаємо поточні фільтри
        if (selectedAgency) {
            searchParams.agency = selectedAgency;
        }

        if (selectedStatus) {
            searchParams.pursuable = selectedStatus === 'pending';
        }

        // Always include confirmed filter
        searchParams.confirmed = showConfirmedImages;

        // Додаємо параметри пагінації
        searchParams.page = (newPagination.current - 1); // Конвертуємо в 0-based
        searchParams.size = newPagination.pageSize;

        // Додаємо сортування
        if (sorter && sorter.field && sorter.order) {
            const sortDirection = sorter.order === 'ascend' ? 'asc' : 'desc';
            searchParams.sort = `${sorter.field},${sortDirection}`;
        } else if (sortByLastModify) {
            // Якщо немає сортування з таблиці, але включене сортування по lastModify
            searchParams.sort = 'lastModify,desc';
            searchParams.excludeNullDates = true;
        }

        setPagination(newPagination);
        searchOriginalImages(searchParams);
    };

    const columns = [
        {
            title: 'Image ID',
            dataIndex: 'imageId',
            key: 'imageId',
            width: 120,
        },
        {
            title: 'Image',
            dataIndex: 'image',
            key: 'image',
            width: 100,
            render: (imageUrl) => (
                <img
                    src={imageUrl}
                    alt="Preview"
                    style={{
                        width: '100%',
                        objectFit: 'cover',
                        borderRadius: '4px',
                        border: '1px solid #d9d9d9'
                    }}
                />
            ),
        },
        {
            title: 'Number of sightings',
            dataIndex: 'sightings',
            key: 'sightings',
            width: 150,
        },
        {
            title: 'Agency',
            dataIndex: 'agency',
            key: 'agency',
            width: 150,
        },
        {
            title: 'Image/sighting status',
            dataIndex: 'status',
            key: 'status',
            width: 180,
            render: (status, record) => (
                <Select
                    value={status}
                    onChange={(value) => handleStatusChange(value, record)}
                    style={{ width: '100%' }}
                    size="small"
                    loading={updatingStatus.has(record.key)}
                    disabled={updatingStatus.has(record.key)}
                >
                    <Option value="pending">Active</Option>
                    <Option value="approved">Not Pursuable</Option>
                </Select>
            ),
        },
        {
            title: 'Confirmed',
            dataIndex: 'confirmed',
            key: 'confirmed',
            width: 120,
            render: (confirmed, record) => (
                <Checkbox
                    checked={confirmed}
                    onChange={(e) => handleConfirmedChange(e.target.checked, record)}
                    disabled={updatingStatus.has(record.key)}
                >
                    {updatingStatus.has(record.key) ? 'Updating...' : 'Confirmed'}
                </Checkbox>
            ),
        },
    ];

    return (
        <div className="image-management-page">
            <div className="page-content">
                <div className="filters-section">
                    <div className="filter-item">
                        <label className="filter-label">Agency</label>
                        <Select
                            placeholder="Select Agency"
                            value={selectedAgency}
                            onChange={handleAgencyChange}
                            style={{ width: '200px' }}
                            allowClear
                            loading={loadingAgencies}
                            showSearch
                            filterOption={(input, option) =>
                                option && option.props && option.props.children &&
                                option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                        >
                            {agencies.map((agency, index) => (
                                <Option key={index} value={agency}>
                                    {agency}
                                </Option>
                            ))}
                        </Select>
                    </div>

                    <div className="filter-item">
                        <label className="filter-label">Status</label>
                        <Select
                            placeholder="Select Status"
                            value={selectedStatus}
                            onChange={handleFilterStatusChange}
                            style={{ width: '200px' }}
                            allowClear
                        >
                            <Option value="pending">Active</Option>
                            <Option value="approved">Not Pursuable</Option>
                        </Select>
                    </div>

                    <div className="filter-item">
                        <label className="filter-label">Options</label>
                        <div className="checkbox-wrapper">
                            <Checkbox
                                checked={showConfirmedImages}
                                onChange={(e) => setShowConfirmedImages(e.target.checked)}
                            >
                                Show all confirmed Images
                            </Checkbox>
                            <Checkbox
                                checked={sortByLastModify}
                                onChange={(e) => handleSortByLastModifyChange(e.target.checked)}
                                style={{ marginLeft: '16px' }}
                            >
                                Sort by last modified
                            </Checkbox>
                        </div>
                    </div>

                    <div className="filter-item">
                        <label className="filter-label" style={{ visibility: 'hidden' }}>Actions</label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <Button
                                type="primary"
                                onClick={handleSearch}
                                style={{ flex: 1 }}
                                size="default"
                                loading={loading}
                            >
                                Search
                            </Button>
                            <Button
                                onClick={handleClearFilters}
                                style={{ flex: 1 }}
                                size="default"
                            >
                                Clear
                            </Button>
                        </div>
                    </div>
                </div>
                <div className="content-section">
                    <Table
                        columns={columns}
                        dataSource={tableData}
                        loading={loading}
                        pagination={{
                            ...pagination,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} of ${total} items`,
                            pageSizeOptions: ['10', '20', '50', '100'],
                        }}
                        onChange={handleTableChange}
                        scroll={{ x: 800 }}
                        size="middle"
                        bordered
                    />
                </div>
            </div>
        </div>
    );
};

export default ImageManagementPage;
